import {
  Box,
  Stack,
  Text,
  Toggle,
  Column,
  Columns,
  useResponsiveValue
} from '@machobear/design-system'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { getFormattedStates } from '@/utils/address'
import useBasic from '@/hooks/use-basic'
import { FieldInput, Dropdown } from '../ds-form'

const PaymentForm = () => {
  const {
    values,
    setFieldValue
  }: {
    values: any
    handleChange: any
    setFieldValue: any
    errors: any
    touched: any
  } = useFormikContext()

  const handleSavePaymentMethod = () => {
    setFieldValue('save_payment_method', !values.save_payment_method)
    setFieldValue('save_billing_address', !values.save_payment_method)
  }

  const { t } = useTranslation()
  const { countries }: any = useBasic(['countries'])
  const formattedStates = getFormattedStates(countries, 'ca')
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  return (
    <Stack space="medium">
      <Stack space="xsmall">
        <Text weight="medium">
          {t('b.checkout.title.credit_card_details', 'Credit card details')}
        </Text>
        <Columns space="xsmall" collapseBelow="tablet">
          <Column>
            <FieldInput
              name="first_name"
              label={t('b.checkout.field.first_name', 'First name')}
              required
            />
          </Column>

          <Column>
            <FieldInput
              name="last_name"
              label={t('b.checkout.field.last_name', 'Last name')}
              required
            />
          </Column>
        </Columns>

        {isMobile ? (
          <>
            <Columns space="xsmall">
              <Column>
                <FieldInput
                  name="card_number"
                  maxLength={19}
                  mask={(value: string) =>
                    value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')
                  }
                  label={t('b.checkout.field.card_number', 'Card number')}
                  required
                />
              </Column>
            </Columns>

            <Columns space="xsmall">
              <Column width="1/2">
                <FieldInput
                  name="expiration_date"
                  mask={(value: string) =>
                    value.replace(/\D/g, '').replace(/(\d{2})(?=\d)/g, '$1/')
                  }
                  label={t('b.checkout.field.expiry', 'MM/YY')}
                  maxLength={5}
                  required
                />
              </Column>
              <Column width="1/2">
                <FieldInput
                  name="security_code"
                  label={t('b.checkout.field.cvc', 'CVC')}
                  maxLength={4}
                  required
                />
              </Column>
            </Columns>
          </>
        ) : (
          <Columns space="xsmall" collapseBelow="tablet">
            <Column width="3/5">
              <FieldInput
                name="card_number"
                maxLength={19}
                mask={(value: string) => value.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ')}
                label={t('b.checkout.field.card_number', 'Card number')}
                required
              />
            </Column>

            <Column width="1/5">
              <FieldInput
                name="expiration_date"
                mask={(value: string) => value.replace(/\D/g, '').replace(/(\d{2})(?=\d)/g, '$1/')}
                label={t('b.checkout.field.expiry', 'MM/YY')}
                maxLength={5}
                required
              />
            </Column>
            <Column width="1/5">
              <FieldInput
                name="security_code"
                label={t('b.checkout.field.cvc', 'CVC')}
                maxLength={4}
                required
              />
            </Column>
          </Columns>
        )}
      </Stack>

      <Box display="flex" flexDirection="column" gap="xsmall">
        <Text weight="medium">{t('b.checkout.title.billing_address', 'Billing Address')}</Text>
        <Columns space="xsmall" collapseBelow="tablet">
          <Column>
            <FieldInput
              name="street1"
              label={t('b.checkout.field.address', 'Street address')}
              required
            />
          </Column>

          <Column>
            <FieldInput
              name="street2"
              label={t('b.checkout.field.address_2', 'Suite no./ Unit no.')}
            />
          </Column>
        </Columns>
        <Columns space="xsmall" collapseBelow="tablet">
          <Column>
            <FieldInput name="city" label={t('b.checkout.field.city', 'City')} required />
          </Column>

          <Column>
            <Dropdown
              name="state"
              label={t('b.checkout.field.province', 'Province')}
              aria-labelledby="state"
              required
              options={formattedStates}
            />
          </Column>
        </Columns>
        <Columns space="xsmall" collapseBelow="tablet">
          <Column>
            <Dropdown
              name="country"
              aria-labelledby="country"
              label={t('b.checkout.field.country', 'Country')}
              required
              disabled
              options={[{ value: 'ca', label: 'Canada' }]}
            />
          </Column>

          <Column>
            <FieldInput name="zip" label={t('b.checkout.field.zip', 'Postal code')} required />
          </Column>
        </Columns>
      </Box>

      <Toggle
        id="save_payment_method"
        label={t('b.checkout.field.save_payment_method', 'Save payment method')}
        on={values.save_payment_method}
        onChange={handleSavePaymentMethod}
      />
    </Stack>
  )
}

export default PaymentForm
